import { byCodeUnit } from "../sort";
import { z } from "zod";
import {
  MAX_CONNECTOR_VERSIONS,
  connectorSeedSchema,
  type ConnectorSeedDocument,
  type SeedFinding,
  type SeedFindingRule,
} from "../types/connectorsAdmin";
import { declaredLoadOf, declaredLoadsOf, declaresWindowReplace } from "./loadModes";

/**
 * One seed validator for every door into the connector catalog (A13).
 *
 * A seed document reaches `connector_definitions` through four doors: the upload dialog, the
 * upload route, `npm run seed:connectors` (which the release runs) and the committed catalog
 * that CI checks. Each used to check something different. The route and the dialog parsed only
 * the envelope (`connectorSeedSchema`, whose form blocks are `unknown`), the CLI parsed nothing,
 * and the SEC-2 and FR-41 rules lived only in the catalog test, so an uploaded document could
 * break a connection form, credential validation or a run while passing every check it met.
 * Now all four call this function and refuse the same documents with the same words.
 *
 * It is pure: no I/O, no clock, no environment. The hub's validator list is passed in
 * (`shared/connectors/hubValidators.ts`), so a test can hand it any list. What it cannot see
 * stays in the catalog test: that an icon FILE exists and that a template's steps have handlers
 * (FR-33), both facts about the repository rather than the document.
 *
 * `seed` is the envelope-parsed document exactly as `connectorSeedSchema` returns it (the form
 * blocks verbatim, so the upsert checksum is unchanged), or null when any finding is an error.
 * Findings carry a dot path into the document and an issue that names identifiers and counts,
 * never a value (SEC-17): a credential default is reported as present, not quoted.
 */

export interface SeedValidationOptions {
  /** The keys connector-hub registers (FR-41). */
  hubValidators: readonly string[];
}

export interface SeedValidation {
  seed: ConnectorSeedDocument | null;
  findings: SeedFinding[];
}

/**
 * The control types the connection form renders (FR-40), restated from
 * `client/src/components/connections/types.ts` because `shared/` does not import the client;
 * `seedValidator.test.ts` asserts the two lists are the same.
 */
export const SEED_CONTROL_TYPES = [
  "text",
  "password",
  "number",
  "url",
  "email",
  "textarea",
  "select",
  "multiselect",
  "checkbox",
  "radio",
  "file",
  "toggle",
  "date",
  "datetime",
  "folderselector",
  "keyvalue",
] as const;

/**
 * #2428: the kinds of definition an added entity may carry, and the drawer fields each kind's
 * rule reads (`shared/connectors/ga4Report.ts` for `ga4_report`). A seed naming a kind must
 * declare exactly these `addEntityFields`.
 */
export const DEFINITION_KINDS = ["ga4_report"] as const;
export const DEFINITION_KIND_FIELDS: Record<(typeof DEFINITION_KINDS)[number], readonly string[]> =
  {
    ga4_report: ["name", "propertyId", "dimensions", "metrics", "dimensionFilter"],
  };

/** Controls whose value is chosen from `options`, so a field without them cannot be filled in. */
const CHOICE_CONTROLS = new Set(["select", "multiselect", "radio"]);

const DEPENDENCY_OPERATORS = [
  "equals",
  "notEquals",
  "in",
  "notIn",
  "isNotEmpty",
  "isEmpty",
] as const;
const DEPENDENCY_EFFECTS = [
  "show",
  "hide",
  "enable",
  "disable",
  "setValue",
  "setOptions",
  "setRequired",
] as const;

/** `POST /api/connectors/:key/validate` on the hub: the path segment IS the registry key. */
const HUB_VALIDATE_URL = /^\/api\/connectors\/([a-z0-9-]+)\/validate$/;

/** The words that make a `requestMapping` key name a credential; add a word here. */
const CREDENTIAL_WORDS = [
  "pass(?:word|phrase|code)?",
  "secret",
  "token",
  "api_?key",
  "private_?key",
  "client_?key",
  "subscription_?key",
  "web_?service_?key",
  "credential",
];
/** A `requestMapping` key that names a credential (SEC-2's literal check). */
const CREDENTIAL_KEY = new RegExp(CREDENTIAL_WORDS.join("|"), "i");
/** Credential-named keys that carry no secret: an address, or the kind of credential. */
const NOT_A_SECRET_KEY = /(url|uri|type|method|mode)$/i;
/** The parts of a template the hub fills from the form or the connector, never from the document. */
const TEMPLATE_FROM_FORM = /\{\{field:[^}]+\}\}|\{\{connectorName\}\}/g;

// ---------------------------------------------------------------------------------------------
// The four form blocks, as real schemas. Loose objects: a key this schema does not know is kept
// and not refused, because a seed written for a newer form must still load; what is refused is
// a key the form READS having the wrong shape, which is what breaks a form or a validation.
// ---------------------------------------------------------------------------------------------

function ruleOf<T extends z.ZodType>(value: T) {
  return z.looseObject({ value, message: z.string() });
}

const validationRulesSchema = z.looseObject({
  required: ruleOf(z.boolean()).optional(),
  minLength: ruleOf(z.number().int().min(0)).optional(),
  maxLength: ruleOf(z.number().int().min(0)).optional(),
  min: ruleOf(z.number()).optional(),
  max: ruleOf(z.number()).optional(),
  pattern: ruleOf(z.string().min(1)).optional(),
  notAfterToday: z.looseObject({ value: z.literal(true), message: z.string().min(1) }).optional(),
  custom: z.array(z.unknown()).optional(),
});

const fieldDefinitionSchema = z.looseObject({
  id: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  controlType: z.enum(SEED_CONTROL_TYPES),
  groupId: z.string().min(1),
  order: z.number().optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  // Required, not defaulted: SEC-2 turns on this flag, so every field says which side it is on.
  isCredential: z.boolean(),
  // 2026-09-28 cred-check: a credential with an expiry a person types once (a client secret, an
  // API token); the connection form then offers "Expires on" beside it. Credentials only.
  expires: z.boolean().optional(),
  // Round 8: a field that says where the stored credential is sent (a host, a base URL, an
  // account or organisation id, a user name). Changing one on a saved connection marks it
  // untested until a connection test passes (`shared/connectors/targetFields.ts`).
  isTarget: z.boolean().optional(),
  // The form reads `validation.required` unguarded, so a field without the object crashes it.
  validation: validationRulesSchema,
  dependencies: z.array(z.unknown()).optional(),
  disabled: z.boolean().optional(),
  // `visible` absent reads as false: the field would never appear.
  visible: z.boolean(),
  isPlatformManaged: z.boolean().optional(),
  options: z.array(z.looseObject({ text: z.string(), value: z.string() })).optional(),
  // #2428: a select whose options are the comma list another (top-level) field holds now, so the
  // Add report drawer offers the connection's own property ids before the first save.
  optionsFromFieldId: z.string().min(1).optional(),
});

const fieldGroupSchema = z.looseObject({
  id: z.string().min(1).max(200),
  label: z.string().min(1).max(200),
  description: z.string().optional(),
  order: z.number().optional(),
  collapsible: z.boolean().optional(),
  defaultExpanded: z.boolean().optional(),
});

const dependencySchema = z.looseObject({
  sourceFieldId: z.string().min(1),
  operator: z.enum(DEPENDENCY_OPERATORS),
  value: z.union([z.string(), z.array(z.string())]).optional(),
  effect: z
    .looseObject({ type: z.enum(DEPENDENCY_EFFECTS) })
    // The form assigns `effect.required` straight to the field's state (useFieldDependencies).
    .refine((effect) => effect.type !== "setRequired" || typeof effect.required === "boolean", {
      message: "A setRequired effect carries a boolean required.",
      path: ["required"],
    }),
});

/** `HubClient.extractJsonPath` answers `undefined` for anything not starting `$.`. */
const responsePathSchema = z
  .string()
  .regex(/^\$\./, 'A response path starts with "$." (for example "$.isSuccess").');

const credentialValidationSchema = z.looseObject({
  endpoint: z.looseObject({
    method: z.string().min(1).optional(),
    urlTemplate: z.string().min(1),
    target: z.string().min(1).optional(),
  }),
  requestMapping: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .optional(),
  responseMapping: z
    .looseObject({
      successIndicator: responsePathSchema.optional(),
      errorMessagePath: responsePathSchema.optional(),
      errorDetailPath: responsePathSchema.optional(),
      entityListPath: responsePathSchema.optional(),
      entityNameField: z.string().min(1).optional(),
      entityDisplayNameField: z.string().min(1).optional(),
    })
    .optional(),
  timeoutMs: z.number().int().min(1).max(600_000).optional(),
});

const entityColumnSchema = z.looseObject({
  id: z.string().min(1),
  label: z.string(),
  field: z.string().min(1),
  width: z.union([z.literal("auto"), z.number().positive()]),
  minWidth: z.number().positive().optional(),
  maxWidth: z.number().positive().optional(),
  applyPrefix: z.boolean().optional(),
  editor: z.looseObject({ type: z.literal("select"), fromFieldId: z.string().min(1) }).optional(),
});

const entitySelectionSchema = z.looseObject({
  entityLabelSingular: z.string().min(1),
  entityLabelPlural: z.string().min(1),
  supportsSelectAll: z.boolean(),
  supportsSearch: z.boolean(),
  supportsIncrementalPerEntity: z.boolean(),
  tableTypeKey: z.string().optional(),
  entityNamePrefix: z.string().optional(),
  columns: z.array(entityColumnSchema).optional(),
  viewToggle: z.boolean().optional(),
  defaultView: z.enum(["all", "selected", "unselected"]).optional(),
  pageSize: z.number().int().positive().optional(),
  supportsAddEntity: z.boolean().optional(),
  addEntityFields: z.array(fieldDefinitionSchema).optional(),
  browsePicker: z
    .looseObject({
      enabled: z.boolean(),
      modeSourceFieldId: z.string().min(1),
      rootPathSourceFieldId: z.string().min(1).optional(),
    })
    .optional(),
  // #2428: what an added entity's definition is (`shared/connectors/ga4Report.ts`), and how every
  // added entity's rows land (the declared entities' `mergeConfig` shape).
  definitionKind: z.enum(DEFINITION_KINDS).optional(),
  addEntityMergeConfig: z.looseObject({ loadMode: z.string().min(1) }).optional(),
});

// ---------------------------------------------------------------------------------------------

type Json = Record<string, unknown>;

function isRecord(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The records of an array, with their index; anything else in it was already a schema finding. */
function recordsOf(value: unknown): Array<[number, Json]> {
  if (!Array.isArray(value)) return [];
  const out: Array<[number, Json]> = [];
  value.forEach((item, index) => {
    if (isRecord(item)) out.push([index, item]);
  });
  return out;
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function pathOf(segments: ReadonlyArray<PropertyKey>): string {
  const path = segments.map((segment) => String(segment)).join(".");
  return path === "" ? "document" : path;
}

class Findings {
  readonly list: SeedFinding[] = [];

  add(severity: SeedFinding["severity"], rule: SeedFindingRule, field: string, issue: string) {
    this.list.push({ severity, rule, field, issue });
  }

  error(rule: SeedFindingRule, field: string, issue: string) {
    this.add("error", rule, field, issue);
  }

  warning(rule: SeedFindingRule, field: string, issue: string) {
    this.add("warning", rule, field, issue);
  }

  /** Every zod issue as a `schema` error, its path under `prefix`. */
  schema(prefix: ReadonlyArray<PropertyKey>, error: z.ZodError) {
    for (const issue of error.issues) {
      this.error("schema", pathOf([...prefix, ...issue.path]), issue.message);
    }
  }
}

/** A block checked against its schema only when present: every block is optional in a seed. */
function checkBlock(findings: Findings, doc: Json, key: string, schema: z.ZodType) {
  if (doc[key] === undefined || doc[key] === null) return;
  const result = schema.safeParse(doc[key]);
  if (!result.success) findings.schema([key], result.error);
}

interface FieldAt {
  path: string;
  field: Json;
}

/** Top-level fields and the add-entity drawer's fields: both are rendered by the same form. */
function fieldsOf(doc: Json): { top: FieldAt[]; addEntity: FieldAt[] } {
  const top = recordsOf(doc.fieldDefinitions).map(([index, field]) => ({
    path: `fieldDefinitions.${index}`,
    field,
  }));
  const selection = isRecord(doc.entitySelection) ? doc.entitySelection : {};
  const addEntity = recordsOf(selection.addEntityFields).map(([index, field]) => ({
    path: `entitySelection.addEntityFields.${index}`,
    field,
  }));
  return { top, addEntity };
}

/** SEC-2: no credential value in a document, and no credential stored as a plain column. */
function checkSec2(
  findings: Findings,
  doc: Json,
  fields: { top: FieldAt[]; addEntity: FieldAt[] },
) {
  const groups = declaredGroups(findings, doc);
  checkFieldIds(findings, fields.top, groups);
  for (const { path, field } of [...fields.top, ...fields.addEntity]) {
    checkCredentialField(findings, path, field);
  }
  checkConstantCredentials(findings, doc);
}

/** SEC-2: the group ids `fieldGroups` declares, each once. */
function declaredGroups(findings: Findings, doc: Json): Set<string> {
  const groups = new Set<string>();
  for (const [index, group] of recordsOf(doc.fieldGroups)) {
    const id = stringOf(group.id);
    if (id === null) continue;
    if (groups.has(id)) {
      findings.error("SEC-2", `fieldGroups.${index}.id`, `Repeats the group id ${id}.`);
    }
    groups.add(id);
  }
  return groups;
}

/** SEC-2: each top-level field id once, and each field in a declared group. */
function checkFieldIds(findings: Findings, top: readonly FieldAt[], groups: ReadonlySet<string>) {
  const seen = new Set<string>();
  for (const { path, field } of top) {
    const id = stringOf(field.id);
    if (id !== null) {
      if (seen.has(id)) findings.error("SEC-2", `${path}.id`, `Repeats the field id ${id}.`);
      seen.add(id);
    }
    const groupId = stringOf(field.groupId);
    if (groupId !== null && !groups.has(groupId)) {
      findings.error(
        "SEC-2",
        `${path}.groupId`,
        `Field ${id ?? path} is in the group ${groupId}, which fieldGroups does not declare, so the form never shows it.`,
      );
    }
  }
}

/**
 * SEC-2: a credential field carries no default value, only a credential expires, and a password
 * control is a credential.
 */
function checkCredentialField(findings: Findings, path: string, field: Json) {
  const id = stringOf(field.id) ?? path;
  const defaultValue = field.defaultValue;
  if (
    field.isCredential === true &&
    defaultValue !== undefined &&
    defaultValue !== null &&
    defaultValue !== ""
  ) {
    // Reported as present, never quoted: the value is the thing that must not travel.
    findings.error(
      "SEC-2",
      `${path}.defaultValue`,
      `Field ${id} is a credential with a default value. A seed never carries a credential; Key Vault holds it.`,
    );
  }
  if (field.expires === true && field.isCredential !== true) {
    findings.error(
      "SEC-2",
      `${path}.expires`,
      `Field ${id} is marked expires but is not a credential; only a credential has an expiry date.`,
    );
  }
  if (field.controlType === "password" && field.isCredential !== true) {
    findings.error(
      "SEC-2",
      `${path}.isCredential`,
      `Field ${id} is a password control that is not marked isCredential, so its value would be stored in a database column.`,
    );
  }
}

/**
 * SEC-2: a credential-named key the hub receives as a constant rather than from a form field. A
 * heuristic on the key name, so a WARNING: the value is never quoted, and a key such as
 * `token_base_url` is a URL, not a secret. Review r1.
 */
function checkConstantCredentials(findings: Findings, doc: Json) {
  const block = isRecord(doc.credentialValidation) ? doc.credentialValidation : {};
  const mapping = isRecord(block.requestMapping) ? block.requestMapping : {};
  for (const [key, template] of Object.entries(mapping)) {
    if (!CREDENTIAL_KEY.test(key) || NOT_A_SECRET_KEY.test(key)) continue;
    const constant =
      typeof template === "string"
        ? template.replace(TEMPLATE_FROM_FORM, "").trim() !== ""
        : template !== null && template !== "";
    if (constant) {
      findings.warning(
        "SEC-2",
        `credentialValidation.requestMapping.${key}`,
        `It sends ${key} as a value written in the document, not from a form field. A seed never carries a credential; send {{field:<id>}} from a credential field.`,
      );
    }
  }
}

/** FR-41: an active connector names a connector-hub validator that exists. */
function checkFr41(findings: Findings, doc: Json, hubValidators: readonly string[]) {
  const active = doc.isActive !== false;
  const severity: SeedFinding["severity"] = active ? "error" : "warning";
  const consequence = active
    ? "No connection to it can be validated, so none can leave draft."
    : "Activate it only together with its validator.";

  const block = doc.credentialValidation;
  if (!isRecord(block)) {
    findings.add(
      severity,
      "FR-41",
      "credentialValidation",
      `The document declares no credential validation. ${consequence}`,
    );
    return;
  }
  const endpoint = isRecord(block.endpoint) ? block.endpoint : null;
  if (endpoint === null) return; // the schema check already reported the missing endpoint

  if (endpoint.target !== "connector-hub") {
    findings.add(
      severity,
      "FR-41",
      "credentialValidation.endpoint.target",
      `Credential validation must target connector-hub. ${consequence}`,
    );
  }
  const urlTemplate = typeof endpoint.urlTemplate === "string" ? endpoint.urlTemplate : "";
  const match = HUB_VALIDATE_URL.exec(urlTemplate);
  if (!match) {
    findings.add(
      severity,
      "FR-41",
      "credentialValidation.endpoint.urlTemplate",
      `The validation URL is not /api/connectors/<validator>/validate. ${consequence}`,
    );
    return;
  }
  const key = match[1] ?? "";
  if (!hubValidators.includes(key)) {
    findings.add(
      severity,
      "FR-41",
      "credentialValidation.endpoint.urlTemplate",
      `It names the validator ${key}, which connector-hub does not register. ${consequence}`,
    );
  }
}

/** FR-32: image tags within the cap, and no refresh the entities cannot carry. */
function checkFr32(findings: Findings, doc: Json) {
  const images = Array.isArray(doc.connectorImages) ? doc.connectorImages : [];
  if (images.length === 0) {
    findings.warning(
      "FR-32",
      "connectorImages",
      "The document pins no image tag. A run cannot start until one is added.",
    );
  }
  if (images.length > MAX_CONNECTOR_VERSIONS) {
    findings.error(
      "FR-32",
      "connectorImages",
      `A connector carries at most ${MAX_CONNECTOR_VERSIONS} image tags; the document lists ${images.length}.`,
    );
  }
  const tags = new Set<string>();
  for (const [index, image] of recordsOf(images)) {
    const tag = stringOf(image.tag);
    if (tag === null) continue;
    if (tags.has(tag)) {
      findings.error("FR-32", `connectorImages.${index}.tag`, `Repeats the image tag ${tag}.`);
    }
    tags.add(tag);
  }

  const selection = isRecord(doc.entitySelection) ? doc.entitySelection : {};
  // #2428: a hand-named connector's `addEntityMergeConfig` counts as every added entity's config.
  const declared = declaredLoadsOf({
    entities: Array.isArray(doc.entities) ? doc.entities : [],
    entitySelection: doc.entitySelection,
  });
  if (
    doc.isIncrementalSupported === true &&
    selection.supportsIncrementalPerEntity !== true &&
    !declaresWindowReplace(declared)
  ) {
    // `buildEntityPlan` takes its full branch for an entity without a watermark, so an
    // "incremental" run would INSERT OVERWRITE each table with one delta window. A connector whose
    // declared entities each say how their window lands (`mergeConfig`, `loadModes.ts`) is loaded
    // by the manifest's `replace_window` instead, so it may offer the refresh (2026-09-30).
    findings.error(
      "FR-32",
      "entitySelection.supportsIncrementalPerEntity",
      "The document offers incremental refresh, but no entity can carry a watermark. Set entitySelection.supportsIncrementalPerEntity, declare every entity's mergeConfig (or a hand-named connector's addEntityMergeConfig) with at least one replace_where window, or turn the refresh off.",
    );
  }
}

/** What the connection form does with the blocks: choices, patterns, dependencies, mappings. */
function checkForm(
  findings: Findings,
  doc: Json,
  fields: { top: FieldAt[]; addEntity: FieldAt[] },
) {
  const topIds = new Set(fields.top.map(({ field }) => stringOf(field.id)).filter(Boolean));
  const drawerIds = new Set(
    fields.addEntity.map(({ field }) => stringOf(field.id)).filter(Boolean),
  );

  for (const [list, known] of [
    [fields.top, topIds],
    [fields.addEntity, drawerIds],
  ] as const) {
    for (const { path, field } of list) checkFormField(findings, path, field, known);
  }
  checkMappedFields(findings, doc, topIds);
  checkColumnEditors(findings, doc, topIds, drawerIds);
  checkOptionsFrom(findings, [...fields.top, ...fields.addEntity], topIds);
  checkDefinitionKind(findings, doc, drawerIds);
}

/** #2428: a select that reads its options from another field names a top-level field. */
function checkOptionsFrom(
  findings: Findings,
  fields: readonly FieldAt[],
  topIds: ReadonlySet<string | null>,
) {
  for (const { path, field } of fields) {
    const from = stringOf(field.optionsFromFieldId);
    if (from === null) continue;
    if (!topIds.has(from)) {
      findings.error(
        "form",
        `${path}.optionsFromFieldId`,
        `Field ${stringOf(field.id) ?? path} reads its options from ${from}, which is not a top-level field of this form.`,
      );
    }
  }
}

/**
 * #2428: a seed that names a `definitionKind` is hand-named with no declared entity, and its
 * drawer declares exactly the fields the kind's rule reads, so every added entity carries a
 * definition the runner can send. `addEntityMergeConfig` is a mode the load can act on.
 * `definitionCheck` is reserved for a later save-time check through connector-hub (DESIGN.md R9)
 * and refused while no path calls it.
 */
function checkDefinitionKind(findings: Findings, doc: Json, drawerIds: ReadonlySet<string | null>) {
  const selection = isRecord(doc.entitySelection) ? doc.entitySelection : {};
  if (selection.definitionCheck !== undefined) {
    findings.error(
      "form",
      "entitySelection.definitionCheck",
      "definitionCheck is reserved for a save-time check that nothing calls yet. Remove it.",
    );
  }
  if (
    selection.addEntityMergeConfig !== undefined &&
    declaredLoadOf(selection.addEntityMergeConfig) === null
  ) {
    findings.error(
      "form",
      "entitySelection.addEntityMergeConfig",
      "addEntityMergeConfig is not a mode the load can act on. Use wipe_and_load, or replace_where with a replaceWhereColumn.",
    );
  }
  const kind = stringOf(selection.definitionKind);
  if (kind === null || !(DEFINITION_KINDS as readonly string[]).includes(kind)) return;
  if (selection.supportsAddEntity !== true) {
    findings.error(
      "form",
      "entitySelection.definitionKind",
      `definitionKind ${kind} needs entitySelection.supportsAddEntity, because every entity is one a person adds.`,
    );
  }
  if (Array.isArray(doc.entities) && doc.entities.length > 0) {
    findings.error(
      "form",
      "entities",
      `definitionKind ${kind} declares no entity: the list holds only what a person adds.`,
    );
  }
  const expected = DEFINITION_KIND_FIELDS[kind as (typeof DEFINITION_KINDS)[number]];
  const actual = [...drawerIds].filter((id): id is string => id !== null).sort(byCodeUnit);
  if (actual.join(",") !== [...expected].sort(byCodeUnit).join(",")) {
    findings.error(
      "form",
      "entitySelection.addEntityFields",
      `definitionKind ${kind} reads the fields ${expected.join(", ")}, and addEntityFields must declare exactly those.`,
    );
  }
}

/** One form field: a choice control has options, a pattern compiles, dependencies resolve. */
function checkFormField(
  findings: Findings,
  path: string,
  field: Json,
  known: ReadonlySet<string | null>,
) {
  const id = stringOf(field.id) ?? path;
  if (
    typeof field.controlType === "string" &&
    CHOICE_CONTROLS.has(field.controlType) &&
    stringOf(field.optionsFromFieldId) === null &&
    !(Array.isArray(field.options) && field.options.length > 0)
  ) {
    findings.error(
      "form",
      `${path}.options`,
      `Field ${id} is a ${field.controlType} with no options, so it cannot be filled in.`,
    );
  }

  const validation = isRecord(field.validation) ? field.validation : {};
  const pattern = isRecord(validation.pattern) ? validation.pattern.value : undefined;
  if (typeof pattern === "string" && !compiles(pattern)) {
    findings.error(
      "form",
      `${path}.validation.pattern.value`,
      `The pattern of field ${id} is not a valid regular expression.`,
    );
  }

  if (!Array.isArray(field.dependencies)) return;
  field.dependencies.forEach((dependency: unknown, index: number) => {
    checkDependency(findings, `${path}.dependencies.${index}`, id, dependency, known);
  });
}

/** Whether a pattern is a valid regular expression. */
function compiles(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

/** One dependency of a field: the current shape, naming a field of the same form. */
function checkDependency(
  findings: Findings,
  at: string,
  id: string,
  dependency: unknown,
  known: ReadonlySet<string | null>,
) {
  if (isRecord(dependency) && "fieldId" in dependency && "condition" in dependency) {
    // The retired Studio shape. The form reads `sourceFieldId` and `operator`, so it
    // never fires: the field keeps its declared visibility and requirement.
    findings.warning(
      "form",
      at,
      `Field ${id} uses the retired {fieldId, condition} dependency shape, which the form ignores. Use {sourceFieldId, operator, value, effect}.`,
    );
    return;
  }
  const parsed = dependencySchema.safeParse(dependency);
  if (!parsed.success) {
    findings.schema([...at.split(".")], parsed.error);
    return;
  }
  if (!known.has(parsed.data.sourceFieldId)) {
    findings.warning(
      "form",
      `${at}.sourceFieldId`,
      `Field ${id} depends on ${parsed.data.sourceFieldId}, which is not a field of this form.`,
    );
  }
}

/** Every `{{field:<id>}}` the credential validation sends names a top-level field. */
function checkMappedFields(findings: Findings, doc: Json, topIds: ReadonlySet<string | null>) {
  const block = isRecord(doc.credentialValidation) ? doc.credentialValidation : {};
  // A malformed `fieldDefinitions` is already a schema error; without a field list every
  // reference would read as undeclared, one warning per mapping (review r2).
  const mapping =
    Array.isArray(doc.fieldDefinitions) && isRecord(block.requestMapping)
      ? block.requestMapping
      : {};
  for (const [key, template] of Object.entries(mapping)) {
    if (typeof template !== "string") continue;
    for (const match of template.matchAll(/\{\{field:([^}]+)\}\}/g)) {
      const fieldId = match[1] ?? "";
      if (!topIds.has(fieldId)) {
        findings.warning(
          "form",
          `credentialValidation.requestMapping.${key}`,
          `It sends the field ${fieldId}, which the form does not declare, so the hub receives it empty.`,
        );
      }
    }
  }
}

/** A column editor that reads its options from a field names a field of the form. */
function checkColumnEditors(
  findings: Findings,
  doc: Json,
  topIds: ReadonlySet<string | null>,
  drawerIds: ReadonlySet<string | null>,
) {
  const selection = isRecord(doc.entitySelection) ? doc.entitySelection : {};
  for (const [index, column] of recordsOf(selection.columns)) {
    const editor = isRecord(column.editor) ? column.editor : null;
    const fromFieldId = editor ? stringOf(editor.fromFieldId) : null;
    if (fromFieldId !== null && !drawerIds.has(fromFieldId) && !topIds.has(fromFieldId)) {
      findings.warning(
        "form",
        `entitySelection.columns.${index}.editor.fromFieldId`,
        `The column editor reads its options from ${fromFieldId}, which is not a field of this form.`,
      );
    }
  }
}

/**
 * A rule's own ERROR on a path replaces the generic schema finding on the same path. A rule's
 * WARNING never does: an inactive connector's FR-41 warning on `credentialValidation` must not
 * hide the schema error that refuses a malformed block, or the document would be accepted.
 */
function dedupe(list: SeedFinding[]): SeedFinding[] {
  const ruled = new Set(
    list.filter((f) => f.rule !== "schema" && f.severity === "error").map((f) => f.field),
  );
  const seen = new Set<string>();
  return list.filter((finding) => {
    if (finding.rule === "schema" && ruled.has(finding.field)) return false;
    const key = `${finding.severity}|${finding.rule}|${finding.field}|${finding.issue}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * The longest connector name a seed may carry. A tenant on its own resources runs the connector
 * as `job-<name>-<k>` (ADR 0010; `<k>` is 6 characters), and Container Apps job names are at most
 * 32 characters (`MAX_JOB_NAME_LENGTH`, `runner/executors/ContainerAppsJobExecutor.ts`).
 */
export const MAX_CONNECTOR_NAME_LENGTH = 21;

export function validateSeed(doc: unknown, options: SeedValidationOptions): SeedValidation {
  const findings = new Findings();

  const envelope = connectorSeedSchema.safeParse(doc);
  if (!envelope.success) findings.schema([], envelope.error);

  if (
    isRecord(doc) &&
    typeof doc.name === "string" &&
    doc.name.length > MAX_CONNECTOR_NAME_LENGTH
  ) {
    findings.error(
      "schema",
      "name",
      `A connector name is at most ${MAX_CONNECTOR_NAME_LENGTH} characters, because a tenant's job is named job-<name>-<6 characters> and job names are at most 32; this one is ${doc.name.length}.`,
    );
  }

  if (isRecord(doc)) {
    checkBlock(findings, doc, "fieldGroups", z.array(fieldGroupSchema));
    checkBlock(findings, doc, "fieldDefinitions", z.array(fieldDefinitionSchema));
    checkBlock(findings, doc, "credentialValidation", credentialValidationSchema);
    checkBlock(findings, doc, "entitySelection", entitySelectionSchema);

    const fields = fieldsOf(doc);
    checkSec2(findings, doc, fields);
    checkFr41(findings, doc, options.hubValidators);
    checkFr32(findings, doc);
    checkForm(findings, doc, fields);
  }

  const list = dedupe(findings.list);
  const refused = !envelope.success || list.some((finding) => finding.severity === "error");
  return { seed: refused ? null : envelope.data, findings: list };
}

/**
 * FR-41 at the activate toggle: the stored `credentialValidation` block checked as if the
 * connector were active. A seed uploaded inactive carries FR-41 only as a warning; turning it on
 * must meet the same rule an active upload meets, or the toggle is a way around it.
 */
export function activationFindings(
  credentialValidation: unknown,
  options: SeedValidationOptions,
): SeedFinding[] {
  const findings = new Findings();
  const doc: Json = { isActive: true, credentialValidation };
  checkBlock(findings, doc, "credentialValidation", credentialValidationSchema);
  checkFr41(findings, doc, options.hubValidators);
  return dedupe(findings.list);
}

/** The error findings, in the order found. */
export function seedErrors(findings: readonly SeedFinding[]): SeedFinding[] {
  return findings.filter((finding) => finding.severity === "error");
}

/** The warning findings, in the order found. */
export function seedWarnings(findings: readonly SeedFinding[]): SeedFinding[] {
  return findings.filter((finding) => finding.severity === "warning");
}
